'use client';

import { useEffect, useRef } from 'react';
import { mountMarimoEditor, unmountMarimoEditor } from '@marimo-team/frontend';
import './gamgee-notebook-global.css';

interface GamgeeNotebookProps {
  serverUrl?: string;
  authToken?: string;
  aiUrl?: string;
  aiModel?: string;
  gamgeeAuthToken?: string;
  className?: string;
}

// Detect DaisyUI theme from HTML element
function getDaisyUITheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';

  const htmlTheme = document.documentElement.getAttribute('data-theme');
  if (htmlTheme === 'dark' || htmlTheme === 'light') {
    return htmlTheme;
  }

  // Check for dark class
  if (document.documentElement.classList.contains('dark')) {
    return 'dark';
  }

  // Fallback to system preference
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }

  return 'light';
}

export function GamgeeNotebook({
  serverUrl = 'http://localhost:2718',
  authToken,
  aiUrl,
  aiModel,
  gamgeeAuthToken,
  className = ''
}: GamgeeNotebookProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || !serverUrl || !authToken) return;

    const theme = getDaisyUITheme();

    const error = mountMarimoEditor(containerRef.current, {
      serverUrl,
      authToken,
      aiUrl,
      aiModel,
      gamgeeAuthToken,
      hideChrome: false,
      hideFilename: true,
      configOverrides: {
        ai: {
          mode: 'agent',
          inline_tooltip: true,
        },
        display: {
          theme,
          cell_output: 'below',
          dataframes: 'rich',
        },
      },
    });

    if (error) {
      console.error('Failed to mount Marimo:', error);
    }

    return () => unmountMarimoEditor();
  }, [serverUrl, authToken, aiUrl, aiModel, gamgeeAuthToken]);

  return (
    <div
      id="root"
      ref={containerRef}
      className={`w-full h-full min-h-0! ${className}`}
    />
  );
}
